// src/shared/holiday-weekly.js — the weekly holiday-outstanding series
// (v129.29, rebuilt accrual-based in v129.34).
//
// Zero dependencies, on purpose: imported by the BROWSER bundle
// (src/holidayTracker) and available to the Worker on the same terms. Same
// arrangement as src/shared/email-recipients.js — the rule that decides a
// number lives in one file, not one copy per side.
//
// ── WHY THIS WAS REBUILT ────────────────────────────────────────────────────
// The first version drew a flat line and stated on the card that adding monthly
// accrual "would count the same entitlement twice". That was an assertion, not
// a finding, and it was wrong. Matt challenged it — "wont holiday go up 2.5
// days per person per month?" — and the Amanah leave export of 1 Aug 2026
// settled it. The 2026 joiners are decisive:
//
//     joined 2026-08-01  ->  balance  2.50     (2.5 x 1 month)
//     joined 2026-07-01  ->  balance  5.00     (2.5 x 2)
//     joined 2026-07-08  ->  balance  4.50     (pro-rated part month)
//     joined 2026-03-01  ->  balance 15.00     (2.5 x 6)
//
// Entitlement is EARNED at 2.5 days a calendar month. It is not 30 days granted
// on 1 January — a March joiner would show 30 under that model, not 15. So
// `holiday_total` is earned-to-date, the liability line GROWS through the year,
// and the past was wrong too: crediting everyone in January with their August
// entitlement overstated the early months by hundreds of days.

export const ACCRUAL_PER_MONTH = 2.5;

// Matt's rule (3 Aug): flag anyone who will END THE YEAR carrying more than
// this if they book nothing further. That is the "go and book a holiday" list.
// 19 Oct 2026 (Matt, "Follow the Amanah way"): 31 became 15, the MOST that
// ALGT's Holiday Policy (UAE, May 2025; Qatar follows the same) lets anyone
// carry into the next year, and carried days must be used within its first
// six months. Every label that names the number reads this constant.
export const CARRY_FLAG_DAYS = 15;

// ── the anchor ──────────────────────────────────────────────────────────────
// `holiday_total` is HR's figure as at the last reconcile, so it is entitlement
// earned BY THEN — not for the year. `balances.updated_at` is when that was.
// Everything is projected from that one point:
//
//     accrued(t) = holiday_total + 2.5 x (calendar months from anchor to t)
//
// backwards as well as forwards, clamped at zero — nobody had negative
// entitlement before they joined.
//
// LIMIT, stated on the card rather than buried here: for anyone whose reconcile
// predates their joining date this is slightly generous early in the year.
// Shift has no `staff.start_date` (the promotion dossier hit the same wall).
// Amanah has Joining Date; importing it would make this exact.
export function monthsBetween(fromYmd, toYmd) {
  const a = String(fromYmd || '').slice(0, 10);
  const b = String(toYmd || '').slice(0, 10);
  if (a.length < 10 || b.length < 10) return 0;
  const ay = +a.slice(0, 4), am = +a.slice(5, 7);
  const by = +b.slice(0, 4), bm = +b.slice(5, 7);
  if (!ay || !by || !am || !bm) return 0;
  // Month BOUNDARIES crossed. Accrual is credited at month end — the model the
  // tracker has always documented — so this is a step, not a smooth rate.
  return (by - ay) * 12 + (bm - am);
}

export function accruedAt(row, ymd) {
  const total = Number(row && row.holiday_total) || 0;
  const anchor = (row && row.updated_at) ? String(row.updated_at).slice(0, 10) : null;
  if (!anchor) return total;                    // no anchor: flat, as before
  return Math.max(0, total + ACCRUAL_PER_MONTH * monthsBetween(anchor, ymd));
}

// ── the definition ──────────────────────────────────────────────────────────
//
//     outstanding(week W) = SUM over staff of
//                           ( accrued by end of W  −  holiday days dated <= end of W )
//
// At the current week this still equals the sum of `current_balance` in the
// table beneath it: the anchor contributes zero months at the reconcile date,
// and the reconcile is recent. That agreement is the property worth protecting
// — a graph that disagrees with the table under it is worse than no graph.
//
// Past and future come out of the SAME walk. A holiday row exists on the rota
// whether it has happened or is booked ahead; what changes across today is our
// confidence, which the chart shows as solid vs dashed, not as a different
// number.
export function weeklyOutstanding(rows, year, asOf) {
  const list = Array.isArray(rows) ? rows : [];
  const yr = parseInt(year, 10);
  if (!yr || isNaN(yr)) return [];

  // Monday-anchored buckets covering the calendar year. Monday is for DISPLAY
  // only — this is a company-wide roll-up, not a per-store rota read, so it is
  // not the Qatar week-anchor question. The dates being bucketed are real
  // calendar dates (shiftDateStr already resolved them, anchor-aware).
  const first = new Date(yr + '-01-01T12:00:00Z');
  first.setUTCDate(first.getUTCDate() - ((first.getUTCDay() + 6) % 7));
  const lastDay = yr + '-12-31';

  const byDate = {};
  list.forEach((r) => {
    if (!r) return;
    const dates = (r.taken_dates || []).concat(r.booked_dates || []);
    dates.forEach((d) => {
      const ds = String(d).slice(0, 10);
      if (ds.slice(0, 4) !== String(yr)) return;
      byDate[ds] = (byDate[ds] || 0) + 1;
    });
  });

  const out = [];
  const cursor = new Date(first);
  let cumulativeTaken = 0;
  let guard = 0;
  while (guard++ < 60) {
    const wkStart = cursor.toISOString().slice(0, 10);
    if (wkStart > lastDay) break;
    const endD = new Date(cursor); endD.setUTCDate(endD.getUTCDate() + 6);
    const wkEnd = endD.toISOString().slice(0, 10);

    let inWeek = 0;
    Object.keys(byDate).forEach((d) => { if (d >= wkStart && d <= wkEnd) inWeek += byDate[d]; });
    cumulativeTaken += inWeek;

    let entitled = 0;
    list.forEach((r) => { if (r) entitled += accruedAt(r, wkEnd); });

    out.push({
      week: wkStart,
      week_end: wkEnd,
      days: inWeek,
      entitled: Math.round(entitled * 10) / 10,
      outstanding: Math.round((entitled - cumulativeTaken) * 10) / 10,
      state: wkEnd < asOf ? 'past' : (wkStart <= asOf ? 'current' : 'future'),
    });

    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return out;
}

// ── the carry-risk list (Matt, 3 Aug; the limit is CARRY_FLAG_DAYS, 15 since 19 Oct 2026) ──
// "Can it flag when they are planned to go over 31 days, so we know when to
// tell them they should book a holiday?" (31 was the limit then.)
//
// Keyed on the PROJECTED YEAR-END balance: what they will be carrying on
// 31 December if they book nothing further. Anyone over the limit needs a
// conversation NOW, while there is still year left to take it in.
//
// Deliberately NOT keyed on crossing the limit at some point mid-year: somebody who
// peaks over it in September having already booked two weeks in November does not
// need chasing, and flagging them trains people to ignore the list.
export function carryRisk(rows, year, opts) {
  const o = opts || {};
  const limit = o.limit != null ? Number(o.limit) : CARRY_FLAG_DAYS;
  const yearEnd = String(year) + '-12-31';
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r) return;
    const taken = (r.taken_dates || []).length;
    const booked = (r.booked_dates || []).length;
    const projected = Math.round((accruedAt(r, yearEnd) - taken - booked) * 10) / 10;
    if (projected > limit) {
      out.push({
        staff_id: r.staff_id != null ? r.staff_id : null,
        name: r.name || null,
        store: r.store || null,
        projected_year_end: projected,
        booked_ahead: booked,
        // Days they must book between now and year end to land ON the limit.
        // The actionable number — "book at least this many".
        must_book: Math.ceil(projected - limit),
      });
    }
  });
  return out.sort((a, b) => b.projected_year_end - a.projected_year_end);
}
